/** `crmEmailBulk` namespace — en (typed against the French source). */

import type { CrmEmailBulkKeys } from './fr';

const crmEmailBulk: CrmEmailBulkKeys = {
  title: 'Draft emails in bulk',
  titleGroup: 'Draft for the group “{{name}}”',
  titleResume: 'Resume a draft review',
  close: 'Close',
  back: 'Back',

  actionSelection: 'Draft for the selection',
  actionSelectionTitle: 'Prepare one email draft per selected contact (nothing is sent automatically)',
  actionGroup: 'Draft for the group',
  actionGroupTitle: 'Prepare one email draft for every member of “{{name}}” (nothing is sent automatically)',
  actionResume: 'Draft reviews',
  actionResumeTitle: 'Reopen the review of a batch of drafts already generated',

  noAutoSend:
    'Nothing is sent automatically. Drafts stay drafts: each one is sent manually, one at a time, from the editor, which runs the CASL / Law 25 consent check.',

  loadingRecipients: 'Loading recipients…',
  recipientsLoadFailed: 'Could not load the recipients.',
  retry: 'Retry',
  recipientsHeading: 'Recipients',
  recipientsCount: '{{count}} recipient(s) will get a draft',
  recipientsNone: 'No eligible recipient.',
  skippedCount: '{{count}} contact(s) skipped',
  skippedNoEmail: 'no email address',
  skippedDnc: 'do not contact',
  skippedUnavailable: 'not found',
  examinedCap: 'Only the first {{max}} contacts of the selection are considered.',
  include: 'Include',
  capTitle: 'At most {{max}} contacts per batch',
  capExplain:
    '{{count}} eligible contacts: a batch is limited to {{max}} contacts to keep the review manageable and spending under control. Untick contacts or keep the first {{max}}, then run another batch for the rest.',
  keepFirst: 'Keep the first {{max}}',
  selectedOfMax: '{{count}} / {{max}} selected',

  settingsHeading: 'Draft settings',
  purpose: 'Purpose',
  purposesLoading: 'Loading purposes…',
  purposesFailed: 'Could not load the purposes.',
  template: 'Template (optional)',
  templateRequired: 'Template',
  templateNone: 'No template — free AI writing',
  templatePick: 'Pick a template…',
  templatesLoading: 'Loading templates…',
  templateBuiltin: 'library',
  templateNeeded: 'Pick a template for the “free template” mode.',
  tone: 'Tone',
  language: 'Language',
  length: 'Length',
  instructions: 'Instructions (optional)',
  instructionsPlaceholder: 'e.g. mention the Montréal event on November 12',
  mode: 'Mode',
  modeAi: 'AI writing',
  modeAiHint: 'One personalised draft per contact (AI credits).',
  modeTemplate: 'Free template',
  modeTemplateHint: 'Merges the template with the contact fields, no AI and no credits.',

  toneProfessional: 'Professional',
  toneWarm: 'Warm',
  toneDirect: 'Direct',
  toneFormal: 'Formal',
  toneFriendly: 'Friendly',
  tonePersuasive: 'Persuasive',
  langFr: 'French',
  langEn: 'English',
  langAr: 'Arabic',
  lengthShort: 'Short',
  lengthMedium: 'Medium',
  lengthLong: 'Long',

  costHeading: 'Estimated cost',
  costLine: '{{count}} contacts × ~{{unit}} credits = ~{{total}} credits',
  costPerContact: 'Each contact is charged separately, only if its draft is generated.',
  costUnknown: 'Cost unavailable right now — the server charges the current price per contact.',
  costUnlimited: 'Unlimited account — no credits will be charged.',
  costFree: 'Free template mode — no AI credits will be charged.',
  balance: 'Available balance: {{balance}} credits',
  balanceLow:
    'Your balance ({{balance}}) is below the estimated cost ({{total}}). Generation stops by itself as soon as credits run out; the remaining contacts are not charged.',
  start: 'Generate {{count}} draft(s)',
  startDisabledCap: 'Reduce the selection to {{max}} contacts at most.',
  readOnly: 'Your role does not allow generating or editing drafts.',

  recentHeading: 'Recent batches',
  recentNone: 'No recent draft batch.',
  recentLoading: 'Loading batches…',
  recentItem: '{{label}} — {{count}} contact(s), {{approved}} approved — {{date}}',
  recentResume: 'Resume review',
  recentSelection: 'Selection',

  runHeading: 'Generating',
  runDoneHeading: 'Generation finished',
  runProgress: '{{done}} / {{total}} processed',
  stop: 'Stop',
  stopping: 'Stopping after the drafts in progress…',
  statusPending: 'Waiting',
  statusRunning: 'Writing…',
  statusDone: 'Draft ready',
  statusFailed: 'Failed',
  statusSkippedNoEmail: 'Skipped — no email',
  statusSkippedDnc: 'Skipped — do not contact',
  statusSkippedUnavailable: 'Skipped — not found',
  statusUnfunded: 'Not generated — insufficient credits',
  statusStopped: 'Not generated — stopped',
  retryOne: 'Retry',
  creditsStopped:
    'Insufficient credits: generation stopped. The remaining contacts were neither generated nor charged. Top up your credits, then run them again.',
  userStopped: 'Generation stopped. The remaining contacts were not generated.',
  failedGeneric: 'Generation failed for this contact.',
  failedUnavailable: 'The writing service is temporarily unavailable.',
  runSummary: '{{done}} draft(s) ready, {{failed}} failure(s), {{skipped}} not generated',
  retryRemaining: 'Run the not-generated again ({{count}})',
  toReview: 'Go to review',

  reviewHeading: 'Draft review',
  reviewLoading: 'Loading drafts…',
  reviewFailed: 'Could not load the drafts of this batch.',
  reviewEmpty: 'No draft in this batch.',
  reviewSummary: '{{approved}} / {{total}} approved',
  approve: 'Approve',
  unapprove: 'Remove approval',
  approved: 'Approved',
  approveAll: 'Approve all',
  sent: 'Sent',
  edit: 'Edit',
  collapse: 'Collapse',
  subject: 'Subject',
  body: 'Message',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Draft saved.',
  saveFailed: 'Could not save the draft.',
  updateFailed: 'Could not update the draft.',
  delete: 'Reject',
  deleteConfirm: 'Confirm rejection',
  deleteFailed: 'Could not delete the draft.',
  openInEditor: 'Open in editor',
  openInEditorTitle: 'Review and send this draft manually from the editor (CASL / Law 25 check)',
  noEmail: 'no email',
  noSubject: '(no subject)',
  refresh: 'Refresh',

  sequenceHeading: 'Sequence (optional)',
  sequenceExplain:
    'Enrol the contacts of the approved drafts in an outreach sequence. The CASL / Law 25 consent check applies to every enrolment; non-compliant contacts are left out.',
  sequencePick: 'Pick a sequence…',
  sequencesLoading: 'Loading sequences…',
  sequencesNone: 'No active sequence.',
  sequencesFailed: 'Could not load the sequences.',
  sequenceAdd: 'Add the approved drafts to a sequence ({{count}})',
  sequenceAdding: 'Enrolling…',
  sequenceResult: '{{enrolled}} enrolled, {{skipped}} left out by the CASL / Law 25 check',
  sequenceFailures: '{{count}} draft(s) could not be added.',
};

export default crmEmailBulk;
